// app/javascript/controllers/dae20/static_grid_drag_controller.js
import { Controller } from "@hotwired/stimulus"
import interact from "interactjs"
import { createConsumer } from "@rails/actioncable"

export default class extends Controller {
  static targets = ["component"]
  static values = {
    cols: Number,
    rows: Number
  }

  connect() {
    console.log("StaticGridDragController connected")

    // InteractJS вешает handlers напрямую на DOM-элементы.
    // При `turbo_stream.replace("component_...")` элементы компонентов пересоздаются,
    // Stimulus targets обновляются, но interact-подписки на новых элементах не появляются.
    // Поэтому после любого turbo-stream рендера пробуем доинициализировать drag/resize.
    this._onAfterStreamRender = this.onAfterStreamRender.bind(this)
    document.addEventListener('turbo:after-stream-render', this._onAfterStreamRender)

    // Отмечаем какие элементы уже инициализировали, чтобы не навешивать interact повторно.
    this._initialized = new WeakSet()

    // Счётчик для отслеживания количества stream renders
    this._streamRenderCount = 0

    this.calculateGridDimensions()
    this.initDraggable()
    this.subscribeToUpdates()

    // Пересчитываем размеры при изменении окна (с debounce)
    this.handleResize = this.debounce(() => {
      this.calculateGridDimensions()
    }, 250)

    window.addEventListener('resize', this.handleResize)

    // Слушаем turbo:before-stream-render, чтобы очистить interact со старых элементов
    this._onBeforeStreamRender = this.onBeforeStreamRender.bind(this)
    document.addEventListener('turbo:before-stream-render', this._onBeforeStreamRender)

    // ResizeObserver для обновления размеров ячеек при изменении размера контейнера
    // (например, после подгрузки lazy-loaded компонентов)
    this._resizeObserver = new ResizeObserver((entries) => {
      if (this._resizeDebounceTimeout) clearTimeout(this._resizeDebounceTimeout)
      this._resizeDebounceTimeout = setTimeout(() => {
        this.calculateGridDimensions()
      }, 100)
    })
    this._resizeObserver.observe(this.element)
  }

  disconnect() {
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('turbo:after-stream-render', this._onAfterStreamRender)
    document.removeEventListener('turbo:before-stream-render', this._onBeforeStreamRender)

    // Очищаем таймеры и обсерверы
    if (this._reinitTimeout) {
      clearTimeout(this._reinitTimeout)
    }
    if (this._resizeDebounceTimeout) {
      clearTimeout(this._resizeDebounceTimeout)
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
    }

    if (this.channel) {
      this.channel.unsubscribe()
    }

    // Снимаем interact handlers со всех текущих targets
    this.componentTargets.forEach(component => {
      try {
        interact(component).unset()
      } catch (_) {
        // no-op
      }
    })
  }

  onBeforeStreamRender(event) {
    console.log('onBeforeStreamRender: cleaning up interact handlers')

    // Увеличиваем счётчик stream renders
    this._streamRenderCount++
    console.log(`Stream render count: ${this._streamRenderCount}`)

    // ДО применения turbo-stream: очищаем interact со ВСЕХ элементов в DOM
    // (включая те, которые вот-вот будут заменены)
    const components = document.querySelectorAll('[data-dae20--static-grid-drag-target="component"]')
    console.log(`Found ${components.length} components to clean up`)

    components.forEach(component => {
      try {
        const componentId = component.dataset.componentId
        console.log(`Unsetting interact for component ${componentId}`)
        interact(component).unset()
      } catch (e) {
        console.warn('Error unsetting interact:', e)
      }
    })

    // Очищаем WeakSet, чтобы новые элементы инициализировались заново
    this._initialized = new WeakSet()
    console.log('WeakSet cleared')

    // Отменяем предыдущий таймер переинициализации, если он есть
    if (this._reinitTimeout) {
      clearTimeout(this._reinitTimeout)
    }

    // Планируем переинициализацию после небольшой задержки
    // (чтобы дождаться завершения всех stream renders)
    this._reinitTimeout = setTimeout(() => {
      console.log('Reinitializing after stream renders completed')
      this._streamRenderCount = 0
      this.initDraggable()
    }, 100)
  }

  onAfterStreamRender(event) {
    // После применения turbo-stream, Stimulus targets могут обновиться асинхронно.
    // Используем requestAnimationFrame чтобы дождаться обновления DOM и Stimulus targets.
    requestAnimationFrame(() => {
      console.log('onAfterStreamRender: reinitializing draggable')
      this.initDraggable()
    })
  }

  // Кэшируем вычисления размеров сетки
  calculateGridDimensions() {
    this.gridCols = this.colsValue || 16
    this.gridRows = this.rowsValue || 16

    const computedStyle = window.getComputedStyle(this.element)
    this.gap = parseFloat(computedStyle.gap) || parseFloat(computedStyle.rowGap) || 0

    // Берём реальный размер треков из CSS Grid — он точно учитывает
    // minmax(), 1fr-распределение и gap. clientWidth/Height даёт
    // неверный результат, когда minmax() ограничивает трек или grid
    // overflow'ит из-за gap (P220 gap-1).
    const colSizes = computedStyle.gridTemplateColumns.split(' ')
    const rowSizes = computedStyle.gridTemplateRows.split(' ')

    this.cellWidth = parseFloat(colSizes[0]) || 24
    this.cellHeight = parseFloat(rowSizes[0]) || 24

    // Fallback: если grid ещё не отрендерился (gridTemplateColumns = 'none')
    if (colSizes[0] === 'none' || isNaN(this.cellWidth)) {
      const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0
      const paddingRight = parseFloat(computedStyle.paddingRight) || 0
      const trackWidth = this.element.clientWidth - paddingLeft - paddingRight
      this.cellWidth = (trackWidth - this.gap * (this.gridCols - 1)) / this.gridCols
    }

    if (rowSizes[0] === 'none' || isNaN(this.cellHeight)) {
      const paddingTop = parseFloat(computedStyle.paddingTop) || 0
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0
      const trackHeight = this.element.clientHeight - paddingTop - paddingBottom
      this.cellHeight = (trackHeight - this.gap * (this.gridRows - 1)) / this.gridRows
    }

    // Если высота строк совпадает с CSS-переменной (нет растяжения 1fr),
    // используем её — это избегает субпиксельных ошибок округления.
    const cssCellHeight = parseFloat(computedStyle.getPropertyValue('--cell-h'))
    if (cssCellHeight && !isNaN(cssCellHeight) && Math.abs(this.cellHeight - cssCellHeight) < 1) {
      this.cellHeight = cssCellHeight
    }

    console.log(`Grid: ${this.gridCols}x${this.gridRows}, Cell: ${this.cellWidth.toFixed(1)}x${this.cellHeight.toFixed(1)}px, Gap: ${this.gap}px`)
  }

  initDraggable() {
    console.log(`initDraggable: found ${this.componentTargets.length} component targets`)

    const viewport = this.element.closest('.canvas-viewport')
    const autoScrollConfig = viewport ? {
      container: viewport,
      margin: 50,
      distance: 20,
      interval: 10
    } : true

    this.componentTargets.forEach(component => {
      const componentId = component.dataset.componentId

      // Не переинициализируем один и тот же DOM-узел
      if (this._initialized?.has(component)) {
        console.log(`Component ${componentId} already initialized, skipping`)
        return
      }

      console.log(`Initializing drag/resize for component ${componentId}`)

      component.classList.add("draggable-component")
      component.dataset.dx = 0
      component.dataset.dy = 0

      interact(component)
        .draggable({
          inertia: true,
          autoScroll: autoScrollConfig,
          ignoreFrom: 'summary, a, button, input, select, textarea, [data-dae20--click-outside-target="panel"]',
          listeners: {
            start: (event) => this.dragStart(event),
            move: (event) => this.dragMove(event),
            end: (event) => this.dragEnd(event)
          }
        })
        .resizable({
          edges: { left: true, right: true, bottom: true, top: true },
          // margin раньше было 12 — на мелких блоках (16×2 ≈ 300×40px)
          // это забирало 24px по высоте при 40px общей высоты, так что
          // весь блок попадал в resize-зону и «drag» случайно работал
          // как «resize», расширяя блок на любое движение мыши.
          margin: 6,
          modifiers: [
            // Убрали restrictEdges — он конфликтовал с динамической высотой grid-контейнера
            interact.modifiers.restrictSize({
              min: { width: this.cellWidth, height: this.cellHeight }
            })
          ],
          inertia: true,
          listeners: {
            start: (event) => this.resizeStart(event),
            move: (event) => this.resizeMove(event),
            end: (event) => this.resizeEnd(event) // <-- Сохраняем ТОЛЬКО здесь!
          }
        })

      this._initialized?.add(component)
      console.log(`Component ${componentId} initialized successfully`)
    })
  }

  // ==================== DRAG ====================

  dragStart(event) {
    const target = event.target
    target.classList.add("dragging")

    // Сохраняем оригинальную позицию И размер для rollback.
    // Без originalColSpan/RowSpan rollback после ошибки сервера
    // сбрасывал size к 1×1 (см. rollbackComponentPosition: `|| 1`),
    // и пользователь терял размер компонента.
    target.dataset.originalGridX = target.dataset.gridX
    target.dataset.originalGridY = target.dataset.gridY
    target.dataset.originalColSpan = target.dataset.colSpan
    target.dataset.originalRowSpan = target.dataset.rowSpan

    target.dataset.dx = 0
    target.dataset.dy = 0

    // Сбрасываем внутреннее состояние InteractJS, иначе при следующем drag
    // оно применит старый offset и компонент "прыгнет"
    target.setAttribute('data-x', '0')
    target.setAttribute('data-y', '0')

    target.style.transform = "translate(0px, 0px)"
    target.style.zIndex = 1000

    // Запоминаем начальную позицию скролла viewport для компенсации drift
    const viewport = this.element.closest('.canvas-viewport')
    this._dragStartScroll = {
      top: viewport ? viewport.scrollTop : window.scrollY,
      left: viewport ? viewport.scrollLeft : window.scrollX
    }
  }

  dragMove(event) {
    const target = event.target

    const dx = (parseFloat(target.dataset.dx) || 0) + event.dx
    const dy = (parseFloat(target.dataset.dy) || 0) + event.dy

    target.dataset.dx = dx
    target.dataset.dy = dy

    // Компенсируем autoScroll viewport, чтобы элемент оставался под курсором
    const viewport = this.element.closest('.canvas-viewport')
    let scrollCompensateX = 0
    let scrollCompensateY = 0
    if (viewport && this._dragStartScroll) {
      scrollCompensateX = viewport.scrollLeft - this._dragStartScroll.left
      scrollCompensateY = viewport.scrollTop - this._dragStartScroll.top
    }

    target.style.transform = `translate(${dx - scrollCompensateX}px, ${dy - scrollCompensateY}px)`
  }

  dragEnd(event) {
    const target = event.target
    target.classList.remove("dragging")
    target.style.zIndex = ""

    // Вычисляем финальную позицию напрямую из визуального положения элемента.
    // Это устойчиво к скроллу и не накапливает ошибку dx/dy.
    const gridRect = this.element.getBoundingClientRect()
    const compRect = target.getBoundingClientRect()

    const gridStyle = window.getComputedStyle(this.element)
    const gridBorderLeft = parseFloat(gridStyle.borderLeftWidth) || 0
    const gridBorderTop = parseFloat(gridStyle.borderTopWidth) || 0
    const gridPaddingLeft = parseFloat(gridStyle.paddingLeft) || 0
    const gridPaddingTop = parseFloat(gridStyle.paddingTop) || 0

    // Относительно начала grid tracks (внутри border и padding контейнера)
    const relX = compRect.left - gridRect.left - gridBorderLeft - gridPaddingLeft
    const relY = compRect.top - gridRect.top - gridBorderTop - gridPaddingTop

    let newGridX = Math.round(relX / (this.cellWidth + this.gap))
    let newGridY = Math.round(relY / (this.cellHeight + this.gap))

    const colSpan = parseInt(target.dataset.colSpan, 10) || 1
    const rowSpan = parseInt(target.dataset.rowSpan, 10) || 1

    // Clamp
    newGridX = Math.max(0, Math.min(this.gridCols - colSpan, newGridX))
    // Разрешаем drag за пределы текущей сетки — backend сам разрулит
    // авто-расширение через compute_auto_grid_rows_with_candidate.
    // Hard limit: GRID_MAX_HEIGHT (256 строк).
    newGridY = Math.max(0, Math.min(255, newGridY))

    console.log(`Dragged to: (${newGridX}, ${newGridY})`)

    // Обновляем UI
    target.dataset.gridX = newGridX
    target.dataset.gridY = newGridY
    this.updateGridPosition(target, newGridX, newGridY, colSpan, rowSpan)

    // Сбрасываем transform
    target.dataset.dx = 0
    target.dataset.dy = 0
    target.style.transform = ""
    this._dragStartScroll = null

    // Сохраняем на сервер (сервер сам разрулит collision через авто-слой)
    this.saveComponentPosition(target, newGridX, newGridY)
  }

  // ==================== RESIZE ====================

  resizeStart(event) {
    const target = event.target
    target.classList.add("resizing")

    // Сохраняем оригинальные значения для возможного rollback
    target.dataset.originalGridX = target.dataset.gridX
    target.dataset.originalGridY = target.dataset.gridY
    target.dataset.originalColSpan = target.dataset.colSpan
    target.dataset.originalRowSpan = target.dataset.rowSpan

    target.style.zIndex = 1000
  }

  resizeMove(event) {
    const target = event.target

    const newWidth = event.rect.width
    const newHeight = event.rect.height
    const deltaX = event.deltaRect.left
    const deltaY = event.deltaRect.top

    // Вычисляем новые размеры в ячейках
    let newColSpan = Math.max(1, Math.round(newWidth / (this.cellWidth + this.gap)))
    let newRowSpan = Math.max(1, Math.round(newHeight / (this.cellHeight + this.gap)))

    let currentGridX = parseInt(target.dataset.gridX, 10) || 0
    let currentGridY = parseInt(target.dataset.gridY, 10) || 0

    // Обработка изменения с левой/верхней стороны
    if (deltaX !== 0 || deltaY !== 0) {
      const deltaCols = Math.round(deltaX / (this.cellWidth + this.gap))
      const deltaRows = Math.round(deltaY / (this.cellHeight + this.gap))

      currentGridX = Math.max(0, currentGridX + deltaCols)
      currentGridY = Math.max(0, currentGridY + deltaRows)
    }

    // Ограничиваем размеры сеткой по ширине; по высоте разрешаем
    // выходить за текущие границы — backend разрулит авто-расширение.
    const maxColSpan = this.gridCols - currentGridX
    newColSpan = Math.min(newColSpan, maxColSpan)

    // Обновляем ТОЛЬКО визуальное отображение (без сохранения!)
    target.style.width = `${newColSpan * this.cellWidth + (newColSpan - 1) * this.gap}px`
    target.style.height = `${newRowSpan * this.cellHeight + (newRowSpan - 1) * this.gap}px`

    // Обновляем data-атрибуты для resizeEnd
    target.dataset.gridX = currentGridX
    target.dataset.gridY = currentGridY
    target.dataset.colSpan = newColSpan
    target.dataset.rowSpan = newRowSpan

    // Обновляем inline grid-стили для визуального отображения
    this.updateGridPosition(target, currentGridX, currentGridY, newColSpan, newRowSpan)
  }

  // Сохранение ТОЛЬКО при отпускании мыши
  resizeEnd(event) {
    const target = event.target
    target.classList.remove("resizing")
    target.style.zIndex = ""

    // Сбрасываем inline стили размеров (CSS Grid возьмёт из классов)
    target.style.width = ""
    target.style.height = ""

    const gridX = parseInt(target.dataset.gridX, 10) || 0
    const gridY = parseInt(target.dataset.gridY, 10) || 0
    const colSpan = parseInt(target.dataset.colSpan, 10) || 1
    const rowSpan = parseInt(target.dataset.rowSpan, 10) || 1

    console.log(`Resized: position (${gridX}, ${gridY}), size (${colSpan}x${rowSpan})`)

    // Сохраняем на сервер ОДИН раз
    this.saveComponentPosition(target, gridX, gridY)
  }

  // ==================== SAVE ====================

  async saveComponentPosition(component, x, y) {
    const componentId = component.dataset.componentId
    const pageId = this.element.dataset.pageIdValue
    const templateId = this.element.dataset.templateIdValue
    const width = parseInt(component.dataset.colSpan, 10) || 1
    const height = parseInt(component.dataset.rowSpan, 10) || 1
    const layer = parseInt(component.dataset.gridLayer, 10) || 0

    if (!componentId || !pageId || !templateId) {
      console.error('Missing required data attributes')
      return
    }

    // Показываем индикатор загрузки
    component.classList.add('opacity-70', 'pointer-events-none')

    const url = `/dae20/workspace/${templateId}/pages/${pageId}/update_component_position`
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          // `application/json` первым — хотим получить осмысленное
          // сообщение об ошибке (например, «Компоненты пересекаются»)
          // в JSON, а не turbo-stream-заглушку.
          'Accept': 'application/json, text/vnd.turbo-stream.html',
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': csrfToken
        },
        body: new URLSearchParams({
          component_id: componentId,
          x, y,
          page_id: pageId,
          template_id: templateId,
          width, height,
          layer
        })
      })

      if (response.ok) {
        const body = await response.json()
        console.log('Position saved successfully', body)

        // Если сервер изменил слой — обновляем UI
        if (body.layer !== undefined && body.layer !== layer) {
          component.dataset.gridLayer = body.layer
          this.showFlashMessage('success', `Уровень компонента изменён на ${body.layer}`)
        } else {
          this.showFlashMessage('success', body.message || 'Позиция сохранена')
        }
      } else {
        // Пробуем вытащить message из JSON-тела ответа
        // (сервер возвращает {status:'error', message:'...'}).
        // Если не получилось — fallback на общий текст.
        let message = 'Ошибка сохранения'
        try {
          const body = await response.json()
          if (body && body.message) message = body.message
        } catch (_) { /* body не JSON — пусть будет fallback */ }

        console.error('Save error:', response.status, message)
        this.showFlashMessage('error', message)
        this.rollbackComponentPosition(component)
        return
      }
    } catch (error) {
      console.error('Save error (network):', error)
      this.showFlashMessage('error', 'Ошибка сохранения')
      this.rollbackComponentPosition(component)
    } finally {
      component.classList.remove('opacity-70', 'pointer-events-none')
    }
  }

  rollbackComponentPosition(component) {
    const x = parseInt(component.dataset.originalGridX, 10) || 0
    const y = parseInt(component.dataset.originalGridY, 10) || 0
    const colSpan = parseInt(component.dataset.originalColSpan, 10) || 1
    const rowSpan = parseInt(component.dataset.originalRowSpan, 10) || 1

    component.dataset.gridX = x
    component.dataset.gridY = y
    component.dataset.colSpan = colSpan
    component.dataset.rowSpan = rowSpan

    this.updateGridPosition(component, x, y, colSpan, rowSpan)
  }

  // ==================== GRID CLASSES ====================

  updateGridPosition(target, gridX, gridY, colSpan, rowSpan) {
    // Убираем устаревшие Tailwind-классы, если вдруг остались
    const classesToRemove = [...target.classList].filter(
      cls => cls.startsWith("col-start-") || cls.startsWith("row-start-") ||
             cls.startsWith("col-span-") || cls.startsWith("row-span-")
    )
    target.classList.remove(...classesToRemove)

    target.style.gridColumn = `${gridX + 1} / span ${colSpan}`
    target.style.gridRow = `${gridY + 1} / span ${rowSpan}`
  }

  // ==================== UTILITIES ====================

  debounce(func, wait) {
    let timeout
    return (...args) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  // ==================== ACTION CABLE ====================

  subscribeToUpdates() {
    const pageId = this.element.dataset.pageIdValue
    if (!pageId) return

    const consumer = createConsumer()

    this.channel = consumer.subscriptions.create(
      { channel: "ComponentUpdatesChannel", page_id: pageId },
      {
        received: (data) => {
          console.log("Received update:", data)
          if (data.action === 'update_position') {
            this.handleComponentPositionUpdate(data)
          } else if (data.action === 'update_settings') {
            this.handleComponentSettingsUpdate(data)
          }
        }
      }
    )
  }

  handleComponentPositionUpdate(data) {
    const component = this.componentTargets.find(
      el => el.dataset.componentId == data.component_id
    )

    if (component) {
      component.dataset.gridX = data.x
      component.dataset.gridY = data.y
      const colSpan = data.width !== undefined ? data.width : (parseInt(component.dataset.colSpan, 10) || 1)
      const rowSpan = data.height !== undefined ? data.height : (parseInt(component.dataset.rowSpan, 10) || 1)
      component.dataset.colSpan = colSpan
      component.dataset.rowSpan = rowSpan
      this.updateGridPosition(component, data.x, data.y, colSpan, rowSpan)

      if (data.layer !== undefined) {
        component.dataset.gridLayer = data.layer
      }
    }
  }

  handleComponentSettingsUpdate(data) {
    const component = this.componentTargets.find(
      el => el.dataset.componentId == data.component_id
    )

    if (component) {
      this.showFlashMessage('info', `Компонент обновлён`)
      component.classList.add('ring-2', 'ring-blue-500')
      setTimeout(() => component.classList.remove('ring-2', 'ring-blue-500'), 2000)
      this.reloadComponent(data.component_id)
    }
  }

  async reloadComponent(componentId) {
    const pageId = this.element.dataset.pageIdValue
    const templateId = this.element.dataset.templateIdValue
    const url = `/dae20/workspace/${templateId}/pages/${pageId}/components/${componentId}/reload`

    try {
      const response = await fetch(url, {
        headers: {
          'Accept': 'text/vnd.turbo-stream.html',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error('Reload error:', error)
    }
  }

  showFlashMessage(type, message) {
    const flashContainer = document.getElementById('flash-messages')
    if (!flashContainer) return

    const colors = {
      success: 'bg-green-500',
      error: 'bg-red-500',
      warning: 'bg-yellow-500',
      info: 'bg-blue-500'
    }

    const flash = document.createElement('div')
    flash.className = `mb-2 p-4 rounded shadow-lg text-white flex items-center justify-between ${colors[type] || colors.info}`
    flash.innerHTML = `
      <span>${message}</span>
      <button class="ml-4 hover:text-gray-200 font-bold text-xl">&times;</button>
    `

    flash.querySelector('button').onclick = () => flash.remove()
    flashContainer.prepend(flash)
    setTimeout(() => flash.remove(), 4000)
  }
}
